import { Layout } from "../Layout/Layout";
import React, { useState, useEffect, useRef } from "react";
import ApplicationModel from "../../Models/ApplicationModel";
import { Form, Button } from "@curtin-dxp/ui";
import { PersonalDetails } from "./PersonalDetails";
import { EmailPhoneDetails } from "./EmailPhoneDetails";
import { AddressDetails } from "./AddressDetails";
import { CitizenshipDetails } from "./CitizenshipDetails";
import { CulturalDetails } from "./CulturalDetails";
import { CourseSelection } from "./CourseSelection";
import { AdditionalComments } from "./AdditionalComments";
import { useLocation, useNavigate } from "react-router-dom";
import * as S from "./styles";
import * as CS from "../Common/styles";
import { DocumentUploads } from "./DocumentUploads";
import { v4 as uuid } from "uuid";
import { isValidGceLevelDateRegex } from './utils/ExamDateValidator';


export const ProvideDetailsComponent = () => {
    const navigate = useNavigate();    
    const location = useLocation();
    const [inputData, setInputData] = useState(location.state?.inputData ?? new ApplicationModel());

    useEffect(() => {
        if (location.state?.inputData && !inputData) {
            Object.assign(inputData, location.state.inputData);
        } else {
            if (!inputData.applicationId){
                setInputData((prevData) => ({
                    ...prevData,
                    applicationId: uuid()
                }));
            }
        }
    }, [location.state, inputData]);

    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const scrollToTopRef = useRef(document.documentElement);

    const handleInputChange = (e) => {
        if (/</.test(e.target.value) || />/.test(e.target.value) || /{/.test(e.target.value) || /}/.test(e.target.value)){
            e.preventDefault();
            return;
        }

        const { id, value, name } = e.target;
        const fieldName = id.trim() !== '' ? id : name;

        if (fieldName === '') return;

        if (fieldName === 'phoneNumber'){
            if (!value.startsWith("+") && value !== ''){
                setInputData((prevData) => ({
                    ...prevData,
                    [fieldName]: `+${value}`
                }));
                return;                
            }
        }
        
        //multi select science subject. Adding every selection to array sliitALEALevelSubject
         if (fieldName === 'sliitALEALevelSubject') {
            setInputData((prevData) => {
            let currentSelections = [];
            currentSelections = prevData[fieldName];
            const isCurrentlySelected = currentSelections.includes(value);
            const updatedSelections = isCurrentlySelected ? currentSelections.filter((item) => item !== value)
                                      : [...currentSelections, value];
              return {
                ...prevData,
                [fieldName]: updatedSelections
              };
          });
          return;
        }
    
        setInputData((prevData) => ({
            ...prevData,
            [fieldName]: value
        }));
    };

    function handleSelection(e, fieldName) {
        if (e?.value === undefined || fieldName === undefined) return;

        var dobValue = '';

        if ((fieldName === "dobDay" || fieldName === "dobYear") & e.value !== '') {
            if (fieldName === "dobDay") {
                if (inputData.dobMonth?.trim() !== '' && inputData.dobYear?.trim() !== '') dobValue = `${inputData.dobYear}-${inputData.dobMonth}-${e.value.length === 2 ? e.value : `0${e.value}`}`;
            }
            if (fieldName === "dobYear") {
                if (inputData.dobDay?.trim() !== '' && inputData.dobMonth?.trim() !== '') dobValue = `${e.value}-${inputData.dobMonth}-${inputData.dobDay.length === 2 ? inputData.dobDay : `0${inputData.dobDay}`}`;
            }
        }        

        setInputData((prevData) => ({
            ...prevData,
            [fieldName]: e.value,
            dob: dobValue !== '' ? dobValue : prevData.dob,
            sliitSpecialization: fieldName === "sliitSpecialization" ? e.value : fieldName === "sliitCourse" ? "" : prevData.sliitSpecialization
        }));
    }

    function handleDOBMonthSelect(e){
        const { id, value } = e;

        setInputData((prevData) => ({
            ...prevData,
            dobMonth: id,
            dobMonthName: value,
            dob: (prevData.dobDay.trim() !== '' && id?.trim() !== '' && prevData.dobYear.trim() !== '') ? `${prevData.dobYear}-${id}-${prevData.dobDay.length === 2 ? prevData.dobDay : `0${prevData.dobDay}`}` : ''
        }));
    }

    const handleCampusSelectionChanged = (selection) => {
        if (selection === undefined) return;

        setInputData((prevData) => ({
          ...prevData,
          campus: selection,
        }));
    }        

    const handleSLIITALEChanged = (value) => {
        setInputData((prevData) => ({
            ...prevData,
            sliitALE: value
        }));
    }    

    function handleFileUploaded(propertyName, fileName, originalFilename, fileSize, status) {
        if (propertyName === undefined || propertyName === undefined) return;

        const originalFilenameField = `${propertyName}OF`;
        const fileSizeField = `${propertyName}FS`;
        const uploadStatusField = `${propertyName}US`;        

        setInputData((prevData) => ({
            ...prevData,
            [propertyName]: fileName,
            [originalFilenameField]: originalFilename,
            [fileSizeField]: fileSize,
            [uploadStatusField]: status
        }));
    }

    function handleRemoveFile(fieldName) {
        const originalFilename = `${fieldName}OF`;
        const fileSize = `${fieldName}FS`;
        const uploadStatus = `${fieldName}US`;
        
        //Clear file related values
        setInputData((prevData) => ({
            ...prevData,
            [fieldName]: '',
            [originalFilename]: '',
            [fileSize]: 0,
            [uploadStatus]: ''
        }));        
    }

    function handleSetFileUploadStatus(fieldName, status){
        const uploadStatus = `${fieldName}US`;

        setInputData((prevData) => ({
            ...prevData,
            [uploadStatus]: status ?? ''
        }));             
    }

    function isValidPhoneNumber(e) {
        const regexp = new RegExp(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/);
        const result = regexp.test(e);
        return result;
      }    

    function formIsValid() {
        return validateForm() === null;
    }

    function validateForm() {
        const validationErrors = [];
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        const sliitSpecializationCourses = ["Bachelor of Engineering (BH-ENGR)", "Bachelor of Computing (B-COMP)"];

        //Rules
        if (inputData.familyName.trim() === '') validationErrors.push("*Family name is required");
        if (inputData.dobDay.trim() === '' || inputData.dobMonth.trim() === '' || inputData.dobYear.trim() === '') validationErrors.push("*Date of birth is required");
        if (inputData.gender.trim() === '') validationErrors.push("*Gender is required");
        if (inputData.emailAddress.trim() === '') validationErrors.push("*Email address is required");
        if (inputData.emailAddress.trim() !== '' && !emailRegex.test(inputData.emailAddress)) { validationErrors.push("*Email address is not valid")}
        if (inputData.confirmEmailAddress.trim() === '') validationErrors.push("*Confirm you email address");
        if (inputData.confirmEmailAddress.trim() !== '' && !emailRegex.test(inputData.confirmEmailAddress)) validationErrors.push("*Confirmation email address is not valid");
        if ((inputData.emailAddress.trim() !== '' && inputData.confirmEmailAddress.trim() !== '') && (inputData.emailAddress !== inputData.confirmEmailAddress)) validationErrors.push("*Email addresses do not match");
        if (inputData.phoneNumber.trim() === '') validationErrors.push("*Phone number is required");
        if (inputData.phoneNumber.trim() !== '' && !isValidPhoneNumber(inputData.phoneNumber.trim())) validationErrors.push("*Invalid phone number")
        if (inputData.addressCountry.trim() === '') validationErrors.push("*Address country is required");
        if (inputData.addressLine1.trim() === '') validationErrors.push("*Address line 1 is required");
        if (inputData.addressSuburbTown.trim() === '') validationErrors.push("*Suburb/Town is required");
        if (inputData.countryOfCitizenship.trim() === '') validationErrors.push("*Country of citizenship is required");
        if (inputData.countryOfBirth.trim() === '') validationErrors.push("*Country of birth is required");
        if (inputData.mainLanguageHome.trim() === '') validationErrors.push("*Main language spoken at home is required");
        if (inputData.campus.trim() === '') validationErrors.push("*Select a campus");

        switch (inputData.campus) {
            case "SLIIT":
                const busadmSelected = inputData.sliitCourse !== "Bachelor of Business Administration (B-BUSADM)";
                if (inputData.sliitCourse.trim() === '') validationErrors.push("*Course selection is required");
                if (sliitSpecializationCourses.includes(inputData.sliitCourse) && inputData.sliitSpecialization.trim() === '') validationErrors.push("*Course specialization is required");                
                if (inputData.sliitALE.trim() === '' && busadmSelected) validationErrors.push("*Advanced Level examination is required");
                if (inputData.sliitALE.trim() !== '') {
                    if (inputData.sliitALE === "Yes" && busadmSelected) {
                        if (inputData.gceALevelExamDate.trim() === '') validationErrors.push("*GCE A Level Examination Date is required")
                        if (inputData.gceALevelExamDate.trim() !== '' && !isValidGceLevelDateRegex(inputData.gceALevelExamDate)) { validationErrors.push("*GCE A Level Examination Date is not valid")}
                        if (inputData.sliitALEALevel.trim() === '') validationErrors.push("*A Level type is required");
                        if (inputData.sliitALEALevel.trim() === "Other A Level (please specify)" && inputData.sliitALEALevelOther === '') validationErrors.push("*Other A Level is required")
                        if (inputData.sliitALEALevelSubject.length === 0) validationErrors.push("*A Level science subject is required");
                        if (inputData.sliitALEALevelSubject.includes('Other (please specify if your subject is not listed)') && inputData.sliitALEAScienceSubjectOther.trim() === '') validationErrors.push("*Other science subject(s) is required");
                    }
                    if (inputData.sliitALE === "No"){
                        if (inputData.sliitALEOtherQualStudying.trim() === '') validationErrors.push("*Qualification you are studying is required");
                    }
                }
                if (inputData.sliitCourseYear.trim() === '') validationErrors.push("*Course commencement year is required");
                if (inputData.sliitSemester.trim() === '') validationErrors.push("*Course semester is required");
                if (inputData.sliitACFP.trim() === '') validationErrors.push("*Curtin University Colombo Academy Curtin Foundation Program is required");
                if (inputData.filePassport === '') validationErrors.push("*Upload your Passport / Official Government ID");
                if (inputData.fileAcademicTranscripts === '') validationErrors.push("*Academic Transcripts are required");
            break;

            case "SEAMEO":
                if (inputData.seameoCourseYear.trim() === '') validationErrors.push("*Course commencement year is required");
                if (inputData.filePassport === '') validationErrors.push("*Upload your Passport / Official Government ID");
                if (inputData.fileBachelorTranscriptsOrigLang.trim() === '') validationErrors.push("*Academic transcripts in original language are required");
                if (inputData.fileAllDegreeAwardCertsOrigLang.trim() === '') validationErrors.push("*All degree award certificates in original language are required");              
                if (inputData.fileEmploymentLetters.trim() === '') validationErrors.push("*Employment Letter/s detailing language teaching experience is/are required");
                if (inputData.fileCV.trim() === '') validationErrors.push("*CV (Curriculum Vitae) is required");
            break;

            default:
                break;
        }

        return (
            validationErrors.length === 0 ? null : 
            <>
                {validationErrors.map((value) => (
                    <span>{value}</span>
                ))}
            </>
        );        
    }        

    const scrollToTop = () => {
        scrollToTopRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    async function handleSubmit(event) {
        event.preventDefault();
        setIsFormSubmitted(true);
        if (!formIsValid()) {            
            scrollToTop();
            return;
        }

        // code to display physics + chemisty + any other other science subjects
        // now subjectString is used instead
        if(inputData.sliitALEALevelSubject !== '') {
            const otherSubject = 'Other (please specify if your subject is not listed)';
            const subjectWithoutOther = inputData.sliitALEALevelSubject.includes(otherSubject) ? inputData.sliitALEALevelSubject.filter(subject => subject !== otherSubject) : inputData.sliitALEALevelSubject;
            const baseSubjectString = subjectWithoutOther.length > 0 ? subjectWithoutOther.join(" + ") + ' + ': '';
            const finalSubjectString =  inputData.sliitALEAScienceSubjectOther !== '' ? baseSubjectString + inputData.sliitALEAScienceSubjectOther : inputData.sliitALEALevelSubject.join(" + ");
            inputData.sliitALEAScienceSubjectString = finalSubjectString;
        }
         
        //Clean up any accidental selections/data
        switch (inputData.campus) {
            case "SLIIT":
                setInputData((prevData) => ({
                    ...prevData,
                    seameoCourseYear: '',
                    seameoTrimester: '',
                    fileBachelorTranscriptsOrigLang: '',
                    fileBachelorTranscriptsOrigLangComments: '',
                    fileBachelorTranscriptsEnglish: '',
                    fileBachelorTranscriptsEnglishComments: '',
                    fileAllDegreeAwardCertsOrigLang: '',
                    fileAllDegreeAwardCertsOrigLangComments: '',
                    fileAllDegreeAwardCertsEnglish: '',
                    fileAllDegreeAwardCertsEnglishComments: '',
                    fileCV: '',
                    fileCVComments: '',
                    fileEmploymentLetters: '',
                    fileEmploymentLettersComments: '',
                    fileOtherSupportingMaterial: '',
                    fileOtherSupportingMaterialComments: ''
                  }));
                break;

            case "SEAMEO":
                setInputData((prevData) => ({
                    ...prevData,
                    sliitCourse: '',
                    sliitSpecialization: '',
                    sliitALE: '',
                    sliitALEALevel: '',
                    sliitALEALevelSubject: '',
                    sliitALEOtherQualStudying: '',
                    sliitCourseYear: '',
                    sliitSemester: '',
                    sliitACFP: '',
                    seameoTrimester: 'Trimester 2A'                
                  }));
                break;
                
            default:
                break;
        }        
        
        navigate('/SubmitApplication', { state: {inputData}});
    }

    return (
        <Layout>
            <CS.ContentContaienr>
                {isFormSubmitted && !formIsValid() && (
                    <>
                        <CS.AlertNotification variant="alert" icon="alert" itemType="small" text="Please complete all required fields." />
                            <S.validationErrors>
                            {validateForm()}
                        </S.validationErrors>      
                    </>
                )}

                <CS.Notification text="Please enter your details below, then click Save &amp; Continue." />

                <S.infoPanel>
                    <CS.InfoPoints>
                        <li>
                            This application form is for applicants who are applying to study at these partner institutions ONLY - Curtin Colombo (Sri Lanka) OR SEAMEO (Vietnam)
                        </li>
                        <li>
                            Please ensure you are aware of the academic and English language requirements of the course you wish to apply for
                        </li>
                        <li>
                            Any documents that you are uploading as part of this application are subject to <a href="https://www.curtin.edu.au/study/applying/scanned-documents-certification-requirements/">Curtin University’s documentation guidelines</a>
                        </li>
                    </CS.InfoPoints>
                    <S.spanRed>*Mandatory fields</S.spanRed>                
                </S.infoPanel>

                <Form>
                    <PersonalDetails onChange={handleInputChange} handleSelection={handleSelection} handleDOBMonthSelect={handleDOBMonthSelect} title={inputData.title} firstName={inputData.firstName} otherGivenNames={inputData.otherGivenNames} preferredGivenName={inputData.preferredGivenName} formalName={inputData.formalName} familyName={inputData.familyName} dobDay={inputData.dobDay} dobMonth={inputData.dobMonth} dobMonthName={inputData.dobMonthName} dobYear={inputData.dobYear} gender={inputData.gender} />
                    <EmailPhoneDetails onChange={handleInputChange} emailAddress={inputData.emailAddress} confirmEmailAddress={inputData.confirmEmailAddress} phoneNumber={inputData.phoneNumber} />
                    <AddressDetails handleSelection={handleSelection} onChange={handleInputChange} addressCountry={inputData.addressCountry} addressLine1={inputData.addressLine1} addressLine2={inputData.addressLine2} addressLine3={inputData.addressLine3} addressSuburbTown={inputData.addressSuburbTown} addressState={inputData.addressState} addressPostcode={inputData.addressPostcode} />
                    <CitizenshipDetails handleSelection={handleSelection} countryOfCitizenship={inputData.countryOfCitizenship} />
                    <CulturalDetails handleSelection={handleSelection} countryOfBirth={inputData.countryOfBirth} mainLanguageHome={inputData.mainLanguageHome} />
                    <CourseSelection handleCampusSelectionChanged={handleCampusSelectionChanged} handleSelection={handleSelection} gceALevelExamDate={inputData.gceALevelExamDate} onChange={handleInputChange} handleSLIITALEChanged={handleSLIITALEChanged} campus={inputData.campus} seameoCourseYear={inputData.seameoCourseYear} seameoTrimester={inputData.seameoTrimester} sliitCourse={inputData.sliitCourse} sliitSpecialization={inputData.sliitSpecialization} sliitALE={inputData.sliitALE} sliitALEALevel={inputData.sliitALEALevel} sliitALEALevelOther={inputData.sliitALEALevelOther} sliitALEALevelSubject={inputData.sliitALEALevelSubject} sliitALEAScienceSubjectOther= {inputData.sliitALEAScienceSubjectOther} sliitCourseYear={inputData.sliitCourseYear} sliitSemester={inputData.sliitSemester} sliitACFP={inputData.sliitACFP} sliitALEOtherQualStudying={inputData.sliitALEOtherQualStudying} />
                    {inputData.campus?.trim() !== '' && (
                        <DocumentUploads handleFileUploaded={handleFileUploaded} onChange={handleInputChange} handleRemoveFile={handleRemoveFile} handleSetFileUploadStatus={handleSetFileUploadStatus} applicationId={inputData.applicationId} filePassportComments={inputData.filePassportComments} filePassportOF={inputData.filePassportOF} filePassportFS={inputData.filePassportFS} filePassportUS={inputData.filePassportUS} fileEnglishProfComments={inputData.fileEnglishProficiencyComments} fileEnglishProficiencyOF={inputData.fileEnglishProficiencyOF} fileEnglishProficiencyFS={inputData.fileEnglishProficiencyFS} fileEnglishProficiencyUS={inputData.fileEnglishProficiencyUS} fileAcademicTranscriptsComments={inputData.fileAcademicTranscriptsComments} fileAcademicTranscriptsOF={inputData.fileAcademicTranscriptsOF} fileAcademicTranscriptsFS={inputData.fileAcademicTranscriptsFS} fileAcademicTranscriptsUS={inputData.fileAcademicTranscriptsUS} campus={inputData.campus} fileBachelorTranscriptsOrigLangComments={inputData.fileBachelorTranscriptsOrigLangComments} fileBachelorTranscriptsOrigLangOF={inputData.fileBachelorTranscriptsOrigLangOF} fileBachelorTranscriptsOrigLangFS={inputData.fileBachelorTranscriptsOrigLangFS} fileBachelorTranscriptsOrigLangUS={inputData.fileBachelorTranscriptsOrigLangUS} fileBachelorTranscriptsEnglishComments={inputData.fileBachelorTranscriptsEnglishComments} fileBachelorTranscriptsEnglishOF={inputData.fileBachelorTranscriptsEnglishOF} fileBachelorTranscriptsEnglishFS={inputData.fileBachelorTranscriptsEnglishFS} fileBachelorTranscriptsEnglishUS={inputData.fileBachelorTranscriptsEnglishUS} fileAllDegreeAwardCertsOrigLangComments={inputData.fileAllDegreeAwardCertsOrigLangComments} fileAllDegreeAwardCertsOrigLangOF={inputData.fileAllDegreeAwardCertsOrigLangOF} fileAllDegreeAwardCertsOrigLangFS={inputData.fileAllDegreeAwardCertsOrigLangFS} fileAllDegreeAwardCertsOrigLangUS={inputData.fileAllDegreeAwardCertsOrigLangUS} fileAllDegreeAwardCertsEnglishComments={inputData.fileAllDegreeAwardCertsEnglishComments} fileAllDegreeAwardCertsEnglishOF={inputData.fileAllDegreeAwardCertsEnglishOF} fileAllDegreeAwardCertsEnglishFS={inputData.fileAllDegreeAwardCertsEnglishFS} fileAllDegreeAwardCertsEnglishUS={inputData.fileAllDegreeAwardCertsEnglishUS} fileEmploymentLettersComments={inputData.fileEmploymentLettersComments} fileEmploymentLettersOF={inputData.fileEmploymentLettersOF} fileEmploymentLettersFS={inputData.fileEmploymentLettersFS} fileEmploymentLettersUS={inputData.fileEmploymentLettersUS} fileCVComments={inputData.fileCVComments} fileCVOF={inputData.fileCVOF} fileCVFS={inputData.fileCVFS} fileCVUS={inputData.fileCVUS} fileOtherSupportingMaterialComments={inputData.fileOtherSupportingMaterialComments} fileOtherSupportingMaterialOF={inputData.fileOtherSupportingMaterialOF} fileOtherSupportingMaterialFS={inputData.fileOtherSupportingMaterialFS} fileOtherSupportingMaterialUS={inputData.fileOtherSupportingMaterialUS} />
                    )}                
                    <AdditionalComments />
                    <S.cmdBar>
                        <Button style={({"color":"white"})} variant="primary" onClick={handleSubmit}>Save &amp; Continue</Button>
                    </S.cmdBar>
                </Form>
            </CS.ContentContaienr>
        </Layout>
    );
}